import React, { useState } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from '@/domains/auth/AuthContext'
import { ThemeProvider } from '@/domains/auth/ThemeContext'
import { NotificationProvider } from '@/domains/notifications/NotificationContext'
import { DevModeProvider } from '@/contexts/DevModeContext'
import MobileLayout from '@/components/MobileLayout'
import GlobalOfflineIndicator from '@/components/GlobalOfflineIndicator'
import SplashScreen from '@/components/SplashScreen'
import Login from '@/domains/auth/Login'
import Signup from '@/domains/auth/Signup'
import AcceptInvitation from '@/domains/auth/AcceptInvitation'
import ResetPassword from '@/pages/ResetPassword'
import Status from './pages/Status'
import Dashboard from './pages/Dashboard'
import Reports from '@/pages/Reports'
import IncidentReport from '@/pages/IncidentReport'
import HourlyReport from '@/pages/HourlyReport'
import Locations from '@/pages/Locations'
import PostOrders from '@/pages/PostOrders'
import Profile from '@/pages/Profile'
import Organizations from '@/pages/Organizations'
import ReceivedNotifications from '@/pages/ReceivedNotifications'

function AppRoutes({ user, logout }: { user: any; logout: () => Promise<void> }) {
  // Show login routes if not authenticated
  if (!user) {
    return (
      <>
        <GlobalOfflineIndicator />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/accept-invitation" element={<AcceptInvitation />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </>
    )
  }

  // USER IS LOGGED IN - SHOW AUTHENTICATED CONTENT
  console.log('🔥 NEW APP VERSION - ROUTING FIXED! 🔥')
  
  return (
    <MobileLayout user={user} logout={logout}>
      <Routes>
        <Route path="/" element={<Status user={user} />} />
        <Route path="/status" element={<Status user={user} />} />
        <Route path="/dashboard" element={<Dashboard user={user} />} />
        <Route path="/stats" element={<Dashboard user={user} />} />
        <Route path="/reports" element={<Reports user={user} />} />
        <Route path="/incident-report" element={<IncidentReport />} />
        <Route path="/hourly-report" element={<HourlyReport />} />
        <Route path="/locations" element={<Locations />} />
        <Route path="/post-orders" element={<PostOrders />} />
        <Route path="/organizations" element={<Organizations />} />
        <Route path="/profile" element={<Profile user={user} logout={logout} />} />
        <Route path="/received-notifications" element={<ReceivedNotifications user={user} />} />
        <Route path="*" element={<Status user={user} />} />
      </Routes>
    </MobileLayout>
  )
}

function AuthenticatedApp() {
  const { user, loading, logout } = useAuth()

  // Don't show separate loading - let the splash screen handle it
  // NotificationProvider is now handled at App level
  return (
    <>
      <GlobalOfflineIndicator />
      <AppRoutes user={user} logout={logout} />
    </>
  )
}

function AppContent() {
  const [showSplash, setShowSplash] = useState(true)
  const { loading } = useAuth()

  const handleSplashComplete = () => {
    setShowSplash(false)
  }

  // Show splash screen during initial load AND while auth is loading
  if (showSplash || loading) {
    return <SplashScreen onComplete={handleSplashComplete} />
  }

  return <NotificationWrapper />
}

function NotificationWrapper() {
  const { user } = useAuth()
  
  return (
    <NotificationProvider user={user}>
      <AuthenticatedApp />
    </NotificationProvider>
  )
}

function App() {
  return (
    <ThemeProvider>
      <DevModeProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </DevModeProvider>
    </ThemeProvider>
  )
}

export default App